import { BoundSelectCardGroupField, Css, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ProjectParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { ReadyPlanConfigCard, ReadyPlanConfigCardDetailContent } from "../../components/ReadyPlanConfigCard";
import { GuidedChangeEventConfigType } from "../../utils";
import {
  SelectConfigurationStep_CheckoutConfigFragment,
  SelectConfigurationStep_ProductOfferingConfigFragment,
  SelectConfigurationStep_StructureFragment,
  useSelectConfigurationStepApplicableConfigsQuery,
  useSelectConfigurationStepQuery,
} from "./2.SelectConfigurationStep.generated";

export type ApplyConfigurationFormInput = {
  selectedConfigType?: GuidedChangeEventConfigType | null;
  selectedConfigId?: string | null;
};

type SelectConfigurationStepProps = { formState: ObjectState<ApplyConfigurationFormInput> };
export function SelectConfigurationStep(props: SelectConfigurationStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useSelectConfigurationStepQuery({ variables: { structureId: projectId } });
  return queryResult(query, ({ structure }) => (
    <div css={Css.df.fdc.gap4.$}>
      <div>
        <h2 css={Css.xl.$}>Select the Configuration to Apply</h2>
        <p css={Css.sm.gray700.$}>
          Choose an existing Product Offering or Checkout configuration to replace the current configuration on this
          structure.
        </p>
      </div>

      {structure.readyPlanConfig && (
        <div css={Css.df.fdc.gap2.$}>
          <h3 css={Css.lg.$}>Current Configuration</h3>
          <ReadyPlanConfigCard
            structure={structure}
            readyPlanConfig={structure.readyPlanConfig}
            options={structure.readyPlanConfig.readyPlanOptions}
          />
        </div>
      )}

      <NewConfigurationSection formState={formState} structure={structure} />
    </div>
  ));
}

type NewConfigurationSectionProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
};
function NewConfigurationSection(props: NewConfigurationSectionProps) {
  const { formState, structure } = props;
  const [search, setSearch] = useState("");
  return (
    <div css={Css.df.fdc.gap3.$}>
      <h3 css={Css.lg.$}>New Configuration</h3>
      <BoundSelectCardGroupField
        field={formState.selectedConfigType}
        label="Type"
        view="list"
        options={[
          {
            value: GuidedChangeEventConfigType.CheckoutConfig,
            label: "Checkout Config",
            description:
              "Use a homeowner-approved configuration created specifically for this structure. Includes project-specific pricing.",
          },
          {
            value: GuidedChangeEventConfigType.ProductOfferingConfig,
            label: "Product Offering Config",
            description:
              "Use a standard product offering from this community. Applies the latest catalog definition and pricing.",
          },
        ]}
        onChange={(value) => {
          formState.selectedConfigType.set(value);
          // Changing the type clears any previously selected config
          formState.selectedConfigId.set(undefined);
        }}
      />
      <NewConfigurationSectionView formState={formState} structure={structure} search={search} setSearch={setSearch} />
    </div>
  );
}

type NewConfigurationSectionViewProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
  search: string;
  setSearch: (search: string) => void;
};
function NewConfigurationSectionView(props: NewConfigurationSectionViewProps) {
  const { formState, structure, search, setSearch } = props;
  const selectedConfigType = useComputed(() => formState.selectedConfigType.value, [formState]);
  const productOfferingId = structure.readyPlanConfig?.productOffering?.id ?? fail("Product offering ID is required");
  const query = useSelectConfigurationStepApplicableConfigsQuery({
    skip: !selectedConfigType,
    variables: {
      search,
      productOfferingId,
      structureId: structure.id,
      includeCheckoutConfigs: selectedConfigType === GuidedChangeEventConfigType.CheckoutConfig,
      includeProductOfferingConfigs: selectedConfigType === GuidedChangeEventConfigType.ProductOfferingConfig,
    },
  });
  return (
    <div css={Css.df.fdc.gap2.$}>
      <div css={Css.df.jcsb.aic.$}>
        <span css={Css.sm.fw6.$}>Configurations</span>
        <SearchBox onSearch={setSearch} />
      </div>
      {!selectedConfigType && (
        <EmptyConfigsState message="Select a configuration type above to see the configurations populated here." />
      )}
      {queryResult(query, ({ checkoutConfigs, productOfferingConfigs }) => (
        <ConfigurationSelector
          structure={structure}
          formState={formState}
          productOfferingConfigs={productOfferingConfigs?.entities ?? []}
          checkoutConfigs={checkoutConfigs?.entities ?? []}
        />
      ))}
    </div>
  );
}

type ConfigurationSelectorProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
  productOfferingConfigs: SelectConfigurationStep_ProductOfferingConfigFragment[];
  checkoutConfigs: SelectConfigurationStep_CheckoutConfigFragment[];
};
function ConfigurationSelector(props: ConfigurationSelectorProps) {
  const { formState, structure, productOfferingConfigs, checkoutConfigs } = props;
  const applicableConfigOptions = useMemo(
    () =>
      [
        // Show active product offerings only
        ...productOfferingConfigs.filter((poc) => poc.productOffering.status.code === ReadyPlanStatus.Active),
        ...checkoutConfigs,
      ].map((configLike) => ({
        value: configLike.id,
        label: configLike?.name ?? "(no name)",
        description: <ReadyPlanConfigCardDetailContent readyPlanConfig={configLike} structure={structure} />,
      })),
    [checkoutConfigs, productOfferingConfigs, structure],
  );
  if (applicableConfigOptions.isEmpty) {
    return <EmptyConfigsState message="There are no configurations available for this structure yet." />;
  } else {
    return (
      <BoundSelectCardGroupField
        view="list"
        labelStyle="hidden"
        options={applicableConfigOptions}
        field={formState.selectedConfigId}
      />
    );
  }
}

function EmptyConfigsState(props: { message: string }) {
  return <div css={Css.df.fdc.gap2.jcc.$}>{props.message}</div>;
}
